/**
 * CSS custom properties for theming the STEWARD widget.
 * Host applications can override these to match their design system.
 * Also defines SVG gradient and halo variables for full theme customizability.
 */
import { css } from 'lit';

export const themeProperties = css`
  :host {
    /* Brand palette:
       Rich Black  #0A0A0A  — Primary (headlines, logomark, text)
       Teal        #608386  — Accent (links, CTAs)
       Dark Teal   #344D4F  — Secondary (header, user bubbles, backgrounds)
       Copper      #D4812F  — Signature (launcher, warmth accents)
       White       #FFFFFF  — Canvas
    */

    /* Colors */
    --steward-primary: #344D4F;
    --steward-accent: #608386;
    --steward-signature: #D4812F;
    --steward-bg: #ffffff;
    --steward-bg-secondary: #f2f5f5;
    --steward-text: #0A0A0A;
    --steward-text-secondary: #608386;
    --steward-border: #d0d9da;
    --steward-user-bg: #fdf0e0;
    --steward-user-text: #0A0A0A;
    --steward-assistant-bg: #F5F0EB;
    --steward-assistant-text: #0A0A0A;
    --steward-error-bg: #fef2f2;
    --steward-error-text: #dc2626;
    /* Light mode: no chip — code (inline + block) reads as monospace
     * prose on the bubble. Color is the palette's text-secondary
     * (a gray shade per palette: soleum teal-gray, lux medium gray,
     * zen warm gray) so it's distinguishable from prose without
     * losing brand alignment. Dark theme keeps its chip via the
     * override below for legibility on dark glass. */
    --steward-code-bg: transparent;
    --steward-code-text: var(--steward-text-secondary);
    --steward-signature-hover: #b86d25;
    /* The controls' text and icon colour: the row below the composer and
     * the floating window controls. They sit on the panel's glass, so this
     * colour must contrast with the panel tint. In light mode the panel is
     * whitish glass, so use signature orange for visibility. Dark mode
     * overrides to white. */
    --steward-header-text: var(--steward-signature, #D4812F);
    /* Foreground color laid on the signature copper background (e.g. the
     * send button icon). Distinct from --steward-header-text because the
     * controls sit on no copper background — these two tokens used to
     * coincidentally match (both white) and were sharing one variable. */
    --steward-on-signature: #ffffff;
    --steward-halo-rgb: 212, 129, 47;
    /* RGB triplet for input/control focus halos (textarea, etc.). Mirrors
     * --steward-header-text by mode so focus accents stay symmetric with
     * the title text — signature in light, white in dark. The default
     * indirects through --steward-halo-rgb so palette-themed widgets pick
     * up the active palette's signature RGB automatically. */
    --steward-focus-rgb: var(--steward-halo-rgb);
    --steward-shadow-rgb: 0, 0, 0;
    --steward-svg-grad-left-edge: #f0c88a;
    --steward-svg-grad-left-mid: #D4812F;
    --steward-svg-grad-right-edge: #e6b570;
    --steward-svg-grad-right-mid: #b86d25;

    /* Typography */
    --steward-font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    /* The mono face, for the two surfaces that use one: fenced/inline code
     * and a thinking step's tool detail. The default is the exact stack
     * those two carried as literals, so a standalone widget renders
     * identically — this exists so an embedder (the console) can hand the
     * panel ITS mono rather than leaving code on the UA's default, which
     * is what <pre> falls to when no family is declared at all. */
    --steward-font-mono: 'SF Mono', Menlo, Consolas, monospace;
    --steward-font-size: 14px;
    --steward-line-height: 1.5;

    /* Layout */
    --steward-border-radius: 14px;
    --steward-border-radius-sm: 8px;
    /* The controls' geometry. Defaults keep the stock look — each control a
     * circle, the window controls' backing a capsule — but they are tokens
     * rather than literals because a host whose design system has no pill
     * geometry could not otherwise square them off: the controls live
     * inside the shadow root,
     * which exposes no ::part() and no <slot>, so a custom property is the
     * only reach a host has. */
    --steward-header-btn-radius: 50%;
    --steward-header-group-radius: 999px;
    --steward-spacing: 12px;
    /* Compact spacing for narrow @container scopes (e.g. embedded mode in a
     * <= 480px host slot). Defined as a token (not a literal in the CSS rule)
     * so it inherits the host's design-system overrides the same way
     * --steward-spacing does. */
    --steward-spacing-compact: 8px;
    --steward-z-index: 99999;
    --steward-width: 400px;
    --steward-height: 600px;
    --steward-launcher-size: 64px;
    /* The composer's RESTING height — what an empty input stands at; the
     * field inside takes it less the surface's 12px of inset. Not its
     * ceiling: growth stops at the field's 120px max-height in
     * steward-input.ts, a separate decision with its own literal. A value
     * above 132px swallows that cap, because a min-height larger than a
     * max-height wins, and the field then stands fixed at this height and
     * scrolls internally. */
    --steward-input-height: 44px;

    /* ── Floating-panel margins ───────────────────────────────────────
     * The flyout panel and expanded-classic panel float against the
     * viewport edge with a subtle margin so the rounded glass corners
     * read as a discrete object rather than docking against the screen
     * edge. Subtle is the brief — 12px gives the panel a "lift" without
     * stealing meaningful space. */
    --steward-flyout-margin: 12px;

    /* ── Panel elevation ──────────────────────────────────────────────
     * The panel's whole box-shadow as one value: two outer layers that
     * lift it off the host page, and the inset top edge that gives the
     * glass its thickness.
     *
     * A token rather than a literal for the same reason as the control
     * geometry above — the panel lives inside the shadow root, which
     * exposes no ::part() and no <slot>, so a custom property is the only
     * reach a host has. The WHOLE value is carried here rather than just
     * the two outer layers because a bare none is not a legal item inside
     * a comma list: split, the elevation could not be turned off without an
     * invalid declaration taking the inset edge down with it.
     *
     * Embedded mode defaults this down to the inset alone — see the
     * :host([mode='embedded']) rule in steward-chat.ts. */
    --steward-panel-shadow:
      0 12px 40px rgba(var(--steward-shadow-rgb), 0.18),
      0 2px 8px rgba(var(--steward-shadow-rgb), 0.08),
      inset 0 1px 0 rgba(255, 255, 255, var(--steward-glass-edge-light));

    /* ── Glass-thickness edge ─────────────────────────────────────────
     * Bright inset top-edge highlight on glass surfaces (panel, FAB,
     * flyout, chip pill, table thead). Set to 0 for a flat-glass look.
     * (The matching --steward-glass-edge-dark bottom-shadow tier was
     * removed when the FAB went from "marble bead" to "flat clear glass"
     * — consumers must not reference it: an undefined var() kills the
     * whole box-shadow declaration at computed-value time.) */
    --steward-glass-edge-light: 0.6;

    /* ── Liquid-Glass design tiers ────────────────────────────────────
     * Three active tiers (panel / bubble / chip) — the section tier was
     * dropped when the body transparency extended to the panel's top edge
     * and through the input container. Tunable per tier — set any *-alpha to 1 +
     * *-blur to 0 to revert to fully opaque on that tier. */

    /* Tier 1 — outermost panel (chat window / flyout). */
    --steward-panel-bg-alpha: 0.60;
    --steward-panel-blur: 20px;
    --steward-panel-bg: color-mix(in srgb, var(--steward-bg) calc(var(--steward-panel-bg-alpha) * 100%), transparent);

    /* Tier 2 — bubbles (message rows). Most opaque so prose stays crisp. */
    --steward-bubble-bg-alpha: 0.88;
    --steward-bubble-blur: 6px;

    /* Tier 4 — chips (header-actions pill). Sits ON TOP of panel-tier
     * glass, so its alpha is calibrated to add visible contrast over an
     * already-translucent surface. */
    --steward-chip-bg-alpha: 0.35;
    --steward-chip-blur: 18px;
    --steward-chip-bg: color-mix(in srgb, var(--steward-bg) calc(var(--steward-chip-bg-alpha) * 100%), transparent);

    /* Tier 4b — FAB launcher disc. In light mode this matches the chip
     * tint (35% black-on-white = darken-on-light, reads as glass). The
     * formula is overridden in dark theme blocks below to flip to a
     * white-on-dark tint at lower alpha — real frosted glass scatters
     * light, so on a dark surface it reads as a LIGHTER milky patch,
     * not darker. The chip pill keeps the standard chip-bg formula
     * because it's small + embedded in the panel; the FAB is the only
     * surface that floats over arbitrary host page colors and needs
     * the inversion. */
    --steward-launcher-bg: var(--steward-chip-bg);

    /* Derived per-surface bubble backgrounds. Each surface still owns
     * its tint via the underlying color variable; only the alpha is
     * uniform per tier. Overriding e.g. --steward-user-bg automatically
     * flows the new tint into the user-bubble glass. */
    --steward-user-bg-glass: color-mix(in srgb, var(--steward-user-bg) calc(var(--steward-bubble-bg-alpha) * 100%), transparent);
    --steward-assistant-bg-glass: color-mix(in srgb, var(--steward-assistant-bg) calc(var(--steward-bubble-bg-alpha) * 100%), transparent);
    --steward-error-bg-glass: color-mix(in srgb, var(--steward-error-bg) calc(var(--steward-bubble-bg-alpha) * 100%), transparent);
  }

  /* Dark theme */
  :host([theme="dark"]) {
    --steward-primary: #4a6d70;
    --steward-bg: #0A0A0A;
    --steward-bg-secondary: #1a1f1f;
    --steward-text: #f1f5f5;
    --steward-text-secondary: #8aabad;
    --steward-border: #2a3536;
    --steward-user-bg: #3d5b5e;
    --steward-user-text: #f1f5f5;
    --steward-assistant-bg: #1a1f1f;
    --steward-assistant-text: #f1f5f5;
    --steward-error-bg: #450a0a;
    --steward-error-text: #fca5a5;
    --steward-code-bg: #1a1f1f;
    --steward-code-text: #d0d9da;
    --steward-signature-hover: #a06020;
    --steward-header-text: #ffffff;
    --steward-halo-rgb: 74, 109, 112;
    --steward-svg-grad-left-edge: #7ab0b3;
    --steward-svg-grad-left-mid: #4a6d70;
    --steward-svg-grad-right-edge: #6da0a3;
    --steward-svg-grad-right-mid: #3d5b5e;
    /* Dark glass: a bright white inner-edge highlight reads as a hard
     * outline instead of glass-thickness. Lower the alpha so the rise
     * is felt as texture, not a sharp top stripe. */
    --steward-glass-edge-light: 0.12;
    /* Dark-mode FAB tint — Option B: brand-warm copper at low alpha.
     * Frosted glass on dark surfaces reads as a lighter patch; using
     * the signature color (instead of neutral white) makes the FAB a
     * unique warm-toned brand marker on dark hosts. var() resolves
     * to the active palette's dark-mode signature so palette-themed
     * widgets pick up the correct warm tint automatically. */
    --steward-launcher-bg: color-mix(in srgb, var(--steward-signature) 8%, transparent);
    /* Dark-mode focus accent — flips to white to match
     * --steward-header-text in dark themes (which is also #ffffff).
     * Symmetric with the controls' colour. */
    --steward-focus-rgb: 255, 255, 255;
  }

  /* Auto theme: follow system preference */
  @media (prefers-color-scheme: dark) {
    :host([theme="auto"]) {
      --steward-primary: #4a6d70;
      --steward-bg: #0A0A0A;
      --steward-bg-secondary: #1a1f1f;
      --steward-text: #f1f5f5;
      --steward-text-secondary: #8aabad;
      --steward-border: #2a3536;
      --steward-user-bg: #3d5b5e;
      --steward-user-text: #f1f5f5;
      --steward-assistant-bg: #1a1f1f;
      --steward-assistant-text: #f1f5f5;
      --steward-error-bg: #450a0a;
      --steward-error-text: #fca5a5;
      --steward-code-bg: #1a1f1f;
      --steward-code-text: #d0d9da;
      --steward-signature-hover: #a06020;
      --steward-header-text: #ffffff;
      --steward-halo-rgb: 74, 109, 112;
      --steward-svg-grad-left-edge: #7ab0b3;
      --steward-svg-grad-left-mid: #4a6d70;
      --steward-svg-grad-right-edge: #6da0a3;
      --steward-svg-grad-right-mid: #3d5b5e;
      --steward-glass-edge-light: 0.12;
      /* Match the explicit-dark FAB tint — see :host([theme="dark"]) */
      --steward-launcher-bg: color-mix(in srgb, var(--steward-signature) 8%, transparent);
      /* Match the explicit-dark focus accent — symmetric with header text */
      --steward-focus-rgb: 255, 255, 255;
    }
  }
`;
